import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { type JobDetail, getJob, inventoryUrl, message, runPath } from "./runsClient";

function json(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

/** One job of a Corpus Run: its lease, attempts, events, the dead letters that are its
 * diagnostic records, and the file it processed (task 4030.8). */
export function RunJobPage() {
  const { runId = "", jobId = "" } = useParams();
  const [job, setJob] = useState<JobDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getJob(runId, jobId)
      .then((found) => {
        if (live) setJob(found);
      })
      .catch((caught: unknown) => {
        if (live) setError(message(caught));
      });
    return () => {
      live = false;
    };
  }, [runId, jobId]);

  if (error) return <p role="alert">Could not load the job: {error}</p>;
  if (!job) return <p>Loading…</p>;

  const fileFilter = job.path ? `?q=${encodeURIComponent(job.path)}` : "";

  return (
    <section aria-labelledby="job-title">
      <p>
        <Link to={`${runPath(job.run_id)}${fileFilter}`}>Back to the run</Link>
      </p>
      <h1 id="job-title">
        {job.stage}@{job.stage_version} · {job.path ?? job.subject_key}
      </h1>
      <dl className="facts">
        <dt>Status</dt>
        <dd className={`status-${job.status}`}>{job.status}</dd>
        <dt>Attempts</dt>
        <dd>
          {job.attempts} (failures {job.failures} of {job.max_failures} allowed)
        </dd>
        <dt>Lease</dt>
        <dd>
          {job.lease_owner
            ? `${job.lease_owner}, expires ${job.lease_expires_at ?? "?"}, ` +
              `last heartbeat ${job.heartbeat_at ?? "never"}`
            : "not leased"}
        </dd>
        <dt>Queue</dt>
        <dd>
          priority {job.priority} · resource class {job.resource_class}
          {job.provider ? ` · provider ${job.provider}` : ""} · created {job.created_at}
          {job.completed_at ? ` · finished ${job.completed_at}` : ""}
        </dd>
        <dt>Reuse</dt>
        <dd>
          {job.reused_from_job_id
            ? `output reused from job ${job.reused_from_job_id}`
            : job.cache_key
              ? `computed (cache key ${job.cache_key.slice(0, 16)}…)`
              : "computed"}
        </dd>
      </dl>

      <h2>File</h2>
      {job.file ? (
        <p data-testid="job-file">
          {job.file.path} · {job.file.category} ({job.file.reason_code}: {job.file.reason}) ·{" "}
          <Link to={inventoryUrl(job.run_id)}>Corpus inventory</Link>
        </p>
      ) : (
        <p>This job does not process an inventory file.</p>
      )}

      <h2>Diagnostic records</h2>
      {job.dead_letters.length === 0 ? (
        <p>No dead letters: the job never exhausted its retries or failed permanently.</p>
      ) : (
        job.dead_letters.map((dead) => (
          <article key={dead.dead_letter_id} aria-label={`Dead letter ${dead.dead_letter_id}`}>
            <p>
              <strong>{dead.error_class}</strong> · {dead.reason} after {dead.attempts} attempts ·{" "}
              {dead.state} · {dead.created_at}
              {dead.resolved_by &&
                ` · resolved by ${dead.resolved_by} at ${dead.resolved_at ?? "?"}: ` +
                  (dead.resolution_reason ?? "")}
            </p>
            <pre>{json(dead.error)}</pre>
          </article>
        ))
      )}

      <h2>Attempts</h2>
      <table className="grid" aria-label="Attempts">
        <thead>
          <tr>
            <th>#</th>
            <th>Worker</th>
            <th>Version</th>
            <th>Started</th>
            <th>Ended</th>
            <th>Outcome</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {job.attempt_history.map((a) => (
            <tr key={a.attempt_no}>
              <td>{a.attempt_no}</td>
              <td>{a.worker_id}</td>
              <td>{a.stage_version}</td>
              <td>{a.started_at}</td>
              <td>{a.ended_at ?? ""}</td>
              <td className={`status-${a.outcome}`}>{a.outcome}</td>
              <td>{a.error ? String(a.error.message ?? json(a.error)) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Events</h2>
      <table className="grid" aria-label="Events">
        <thead>
          <tr>
            <th>#</th>
            <th>Event</th>
            <th>Attempt</th>
            <th>Actor</th>
            <th>At</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {job.events.map((e) => (
            <tr key={e.seq}>
              <td>{e.seq}</td>
              <td>{e.event}</td>
              <td>{e.attempt_no ?? ""}</td>
              <td>{e.actor}</td>
              <td>{e.at}</td>
              <td>{Object.keys(e.details).length > 0 ? json(e.details) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Payload and result</h2>
      <pre aria-label="Payload">{json(job.payload)}</pre>
      {job.result && <pre aria-label="Result">{json(job.result)}</pre>}
      {job.last_error && <pre aria-label="Last error">{json(job.last_error)}</pre>}
    </section>
  );
}
